import { IconArrowBack } from "@tine/designsystem-icons/sharp";
import { Group } from "@tine/designsystem-ui-react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { observer } from "mobx-react-lite";
import type { ContentfulAdventCalendarSlot } from "../../../common/types/cmsTypes";
import { isDateInTheFuture } from "../../../common/utils/dateUtils";
import authStore from "../../../stores/auth/authStore";
import { InternalLink } from "../../Links";
import RichTextRenderer from "../RichTextRenderer";
import AdventCalendarContest from "./AdventCalendarContest";
import { AdventCalendarDoorContext } from "./AdventCalendarDoorContext";
import AdventCalendarReminderSignupModal from "./AdventCalendarReminderModal";
import AdventCalendarSticky from "./AdventCalendarSticky";
import type { AdventCalendarCampaign } from "./adventCalendarUtils";

type AdventCalendarArticleContentProps = {
   adventCalendarArticleContent: ContentfulAdventCalendarSlot;
   isPreview?: boolean;
   campaign?: AdventCalendarCampaign;
   isBeforeOpening: boolean;
};

const AdventCalendarArticleContent = observer(
   ({ adventCalendarArticleContent, isPreview = false, campaign, isBeforeOpening }: AdventCalendarArticleContentProps) => {
      const isLoggedIn = authStore.isLoggedIn();
      const { title, date: dateString, body } = adventCalendarArticleContent.fields;
      const date = dateString ? parseISO(dateString) : undefined;
      const isFutureDoor = !!date && isDateInTheFuture(date);
      const showContent = isPreview || !isFutureDoor;
      const canParticipate = isPreview || !!campaign?.slotIds.includes(adventCalendarArticleContent.sys.id);

      return (
         <>
            {campaign && (
               <>
                  <AdventCalendarReminderSignupModal campaign={campaign} />
                  {isLoggedIn && <AdventCalendarSticky campaign={campaign} isBeforeOpening={isBeforeOpening} />}
               </>
            )}
            <div className="relative flex justify-center pb-4 sm:pb-8">
               <div className="absolute top-0 left-0">
                  <InternalLink href="/kampanjer/julekalender" icon={<IconArrowBack />}>
                     Tilbake til julekalenderen
                  </InternalLink>
               </div>
               <h1 className="mt-16 font-medium text-8xl font-suomi sm:pt-0">
                  {date ? format(date, "d. MMMM", { locale: nb }) : title}
               </h1>
            </div>

            <Group>
               {!showContent && <p>Denne luken er ikke åpen ennå. Gå tilbake til julekalenderen og prøv en annen luke.</p>}
            </Group>

            {showContent && (
               <>
                  {canParticipate && <AdventCalendarContest content={adventCalendarArticleContent} />}

                  {body && (
                     <div className="pb-12">
                        <AdventCalendarDoorContext.Provider
                           value={date && canParticipate ? adventCalendarArticleContent : undefined}
                        >
                           <RichTextRenderer
                              key={JSON.stringify(body)}
                              context="standard-article"
                              document={body}
                              trackingLocationName="Advent calendar article"
                           />
                        </AdventCalendarDoorContext.Provider>
                     </div>
                  )}
               </>
            )}
         </>
      );
   }
);

export default AdventCalendarArticleContent;
